/**
 * LanguageDropdown — Custom dropdown component for subtitle language selection
 * Matches the design of header navigation dropdowns
 */

import { useState, useRef, useEffect } from 'react';
import type { Language } from '../definitions/types';
import style from '../style.module.css';

interface Props {
  languages: Language[];
  currentLang: string;
  onSelectLang: (langCode: string) => void;
}

const LanguageDropdown = ({ languages, currentLang, onSelectLang }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const currentLanguage = languages.find(l => l.lang_code === currentLang);
  const currentLangName = currentLanguage?.lang_name.toUpperCase() || 'ENGLISH';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectLanguage = (langCode: string) => {
    onSelectLang(langCode);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={style.languageDropdownWrapper}>
      <button
        ref={buttonRef}
        className={style.languageDropdownButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {currentLangName}
        <svg
          className={style.languageDropdownCaret}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="4 2 8 6 4 10" />
        </svg>
      </button>

      {isOpen && (
        <ul className={style.languageDropdownMenu}>
          {languages.map(lang => (
            <li key={lang.lang_code}>
              <button
                className={style.languageDropdownItem}
                onClick={() => handleSelectLanguage(lang.lang_code)}
                aria-selected={lang.lang_code === currentLang}
              >
                {lang.lang_name.toUpperCase()}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageDropdown;
